/**
 * Расчеты для работы с датами
 */

/**
 * Расчет разницы между двумя датами
 * 
 * @param {Date} startDate - Начальная дата
 * @param {Date} endDate - Конечная дата
 * @returns {Object} Разница в разных единицах измерения
 */
export const calculateDateDifference = (startDate, endDate) => {
  // Убедимся, что работаем с объектами Date
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Разница в миллисекундах
  const diffMs = Math.abs(end - start);
  
  // Разница в днях
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  // Разница в неделях и оставшихся днях
  const diffWeeks = Math.floor(diffDays / 7);
  const remainingDays = diffDays % 7;
  
  // Расчет разницы в месяцах
  let months = (end.getFullYear() - start.getFullYear()) * 12;
  months += end.getMonth() - start.getMonth();
  
  // Корректировка, если день конечной даты меньше дня начальной даты
  if (end.getDate() < start.getDate()) {
    months--;
  }
  
  // Расчет разницы в годах и оставшихся месяцах
  const diffYears = Math.floor(months / 12);
  const remainingMonths = months % 12;
  
  return {
    milliseconds: diffMs,
    seconds: Math.floor(diffMs / 1000),
    minutes: Math.floor(diffMs / (1000 * 60)),
    hours: Math.floor(diffMs / (1000 * 60 * 60)),
    days: diffDays,
    weeks: diffWeeks,
    remainingDays,
    months,
    years: diffYears,
    remainingMonths,
    formatted: {
      short: `${diffDays} д.`,
      medium: diffDays === 0 
        ? 'сегодня'
        : diffDays < 7 
          ? `${diffDays} дн.` 
          : diffWeeks < 4 
            ? `${diffWeeks} нед. ${remainingDays > 0 ? remainingDays + ' дн.' : ''}` 
            : months < 12 
              ? `${months} мес.`
              : `${diffYears} г. ${remainingMonths > 0 ? remainingMonths + ' мес.' : ''}`,
      long: diffDays === 0 
        ? 'сегодня'
        : diffDays === 1 
          ? '1 день'
          : diffDays < 7 
            ? `${diffDays} дней` 
            : diffWeeks === 1 
              ? '1 неделя'
              : diffWeeks < 4 
                ? `${diffWeeks} недель ${remainingDays > 0 ? remainingDays + ' дней' : ''}`
                : months === 1 
                  ? '1 месяц'
                  : months < 12 
                    ? `${months} месяцев`
                    : diffYears === 1 
                      ? '1 год'
                      : `${diffYears} лет ${remainingMonths > 0 ? remainingMonths + ' месяцев' : ''}`
    }
  };
};

/**
 * Добавление дней к дате
 * 
 * @param {Date} date - Исходная дата
 * @param {number} days - Количество дней для добавления (может быть отрицательным)
 * @returns {Date} Новая дата
 */
export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Добавление рабочих дней к дате (без учета выходных)
 * 
 * @param {Date} date - Исходная дата
 * @param {number} days - Количество рабочих дней для добавления
 * @param {Array} holidays - Массив выходных дат (опционально)
 * @returns {Date} Новая дата
 */
export const addWorkingDays = (date, days, holidays = []) => {
  // Преобразуем все праздники в строковый формат для сравнения
  const holidayStrings = holidays.map(h => new Date(h).toISOString().split('T')[0]);
  
  let result = new Date(date);
  let remainingDays = days;
  
  while (remainingDays > 0) {
    result.setDate(result.getDate() + 1);
    
    // Проверяем, что день не выходной (суббота-воскресенье) и не праздник
    const dayOfWeek = result.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 - воскресенье, 6 - суббота
    const isHoliday = holidayStrings.includes(result.toISOString().split('T')[0]);
    
    if (!isWeekend && !isHoliday) {
      remainingDays--;
    }
  }
  
  return result;
};

/**
 * Получение списка дат между двумя датами
 * 
 * @param {Date} startDate - Начальная дата
 * @param {Date} endDate - Конечная дата
 * @returns {Array} Массив дат
 */
export const getDatesBetween = (startDate, endDate) => {
  const dates = [];
  const currentDate = new Date(startDate);
  const lastDate = new Date(endDate);
  
  // Убедимся, что даты в правильном порядке
  if (currentDate > lastDate) {
    return getDatesBetween(endDate, startDate);
  }
  
  while (currentDate <= lastDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

/**
 * Расчет количества рабочих дней между двумя датами
 * 
 * @param {Date} startDate - Начальная дата
 * @param {Date} endDate - Конечная дата
 * @param {Array} holidays - Массив выходных дат (опционально)
 * @returns {number} Количество рабочих дней
 */
export const getWorkingDaysBetween = (startDate, endDate, holidays = []) => {
  // Преобразуем все праздники в строковый формат для сравнения
  const holidayStrings = holidays.map(h => new Date(h).toISOString().split('T')[0]);
  
  // Получаем все даты между начальной и конечной
  const allDates = getDatesBetween(startDate, endDate);
  
  // Фильтруем только рабочие дни
  const workingDays = allDates.filter(date => {
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 - воскресенье, 6 - суббота
    const isHoliday = holidayStrings.includes(date.toISOString().split('T')[0]);
    
    return !isWeekend && !isHoliday;
  });
  
  return workingDays.length;
};

/**
 * Форматирование даты
 * 
 * @param {Date} date - Дата для форматирования
 * @param {string} format - Формат ('short', 'medium', 'long', 'full')
 * @returns {string} Отформатированная дата
 */
export const formatDate = (date, format = 'medium') => {
  const d = new Date(date);
  
  // Получаем компоненты даты
  const day = d.getDate();
  const month = d.getMonth();
  const year = d.getFullYear();
  
  // Названия месяцев
  const monthNames = {
    short: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
    long: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
  };
  
  // Названия дней недели
  const weekdayNames = {
    short: ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'],
    long: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота']
  };
  
  // Форматы даты
  switch (format) {
    case 'short':
      return `${day}.${month + 1}.${year}`;
    
    case 'medium':
      return `${day} ${monthNames.short[month]} ${year}`;
    
    case 'long':
      return `${day} ${monthNames.long[month]} ${year}`;
    
    case 'full':
      return `${weekdayNames.long[d.getDay()]}, ${day} ${monthNames.long[month]} ${year}`;
    
    default:
      return d.toLocaleString('ru-RU');
  }
};

/**
 * Расчет возраста на основе даты рождения
 * 
 * @param {Date} birthDate - Дата рождения
 * @returns {number} Возраст в годах
 */
export const calculateAge = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
};